import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Select from "react-select";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import withLoader from "../../components/layouts/WithLoader";
import SocieteService from "../../services/Societe";
import KPISaleService from "../../services/KPISaleService";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";
import KpiSaleB2B from "./Table/KpiSaleB2B";
import {
  fmt3,
  byIdMonth,
  byMonth,
  computePrevision,
  mergedSum,
  buildChart,
  chartOpts,
  evolution,
} from "./kpiChartUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const unwrap = (r) => r?.data?.success || r?.data || [];

const EvoBadge = ({ curr, prev }) => {
  const pct = evolution(curr, prev);
  if (pct === null) return null;
  const up = pct >= 0;
  return (
    <small style={{ fontSize: "0.75rem", color: up ? "#4caf50" : "#f44336", fontWeight: 600 }}>
      <i className={`fas fa-arrow-${up ? "up" : "down"} mr-1`}></i>
      {Math.abs(pct).toFixed(1)}%
    </small>
  );
};

const ChartCard = ({ title, children }) => (
  <div className="col-md-6">
    <div className="card card-dark">
      <div className="card-header">
        <h3 className="card-title">{title}</h3>
      </div>
      <div className="card-body">{children}</div>
    </div>
  </div>
);

const KPISale = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [selectedYear, setSelectedYear] = useState({ value: currentYear, label: currentYear });
  const [yearsOptions, setYearsOptions] = useState([]);
  const [dashLoading, setDashLoading] = useState(false);
  const [error, setError] = useState("");

  const [factureCA, setFactureCA] = useState([]);
  const [bdcCA, setBdcCA] = useState([]);
  const [facturePayed, setFacturePayed] = useState([]);
  const [devisCounts, setDevisCounts] = useState([]);
  const [bdcCounts, setBdcCounts] = useState([]);

  const [factureCA_p, setFactureCA_p] = useState([]);
  const [bdcCA_p, setBdcCA_p] = useState([]);
  const [facturePayed_p, setFacturePayed_p] = useState([]);
  const [devisCounts_p, setDevisCounts_p] = useState([]);
  const [bdcCounts_p, setBdcCounts_p] = useState([]);

  const fetchDashboard = (y) => {
    const uid = getCurrentUserId();
    const yp = y - 1;
    setYear(y);
    setError("");
    setDashLoading(true);
    startLoading();
    Promise.all([
      KPISaleService.getToByYear(uid, y),
      KPISaleService.getBDCToByYear(uid, y),
      KPISaleService.numberFacturePayed(uid, y),
      KPISaleService.numberDevisMonthsInYear(uid, y),
      KPISaleService.numberBDCMonthsInYear(uid, y),
      KPISaleService.getToByYear(uid, yp),
      KPISaleService.getBDCToByYear(uid, yp),
      KPISaleService.numberFacturePayed(uid, yp),
      KPISaleService.numberDevisMonthsInYear(uid, yp),
      KPISaleService.numberBDCMonthsInYear(uid, yp),
    ])
      .then(([r1, r2, r3, r4, r5, p1, p2, p3, p4, p5]) => {
        setFactureCA(unwrap(r1));
        setBdcCA(unwrap(r2));
        setFacturePayed(unwrap(r3));
        setDevisCounts(unwrap(r4));
        setBdcCounts(unwrap(r5));

        setFactureCA_p(unwrap(p1));
        setBdcCA_p(unwrap(p2));
        setFacturePayed_p(unwrap(p3));
        setDevisCounts_p(unwrap(p4));
        setBdcCounts_p(unwrap(p5));
      })
      .catch(() => setError("Erreur : impossible de charger les KPI de vente"))
      .finally(() => {
        setDashLoading(false);
        stopLoading();
      });
  };

  useEffect(() => {
    SocieteService.getFinacialYearsAndMonthsFromSocieteCreationDate()
      .then((response) => {
        const years = response?.data?.success?.years;
        if (years) {
          setYearsOptions(years.map((y, index) => ({ value: index, label: y })));
        }
      })
      .catch((e) => console.log("error: " + e));
    fetchDashboard(currentYear);
  }, []);

  // ── year N ──
  const factureHT = byIdMonth(factureCA, "totalAmountSellingPrice");
  const factureTT = byIdMonth(factureCA, "totalAmountPriceTT");
  const bdcHT = byIdMonth(bdcCA, "totalAmountSellingPrice");
  const bdcTT = byIdMonth(bdcCA, "totalAmountPriceTT");
  const devisArr = byMonth(devisCounts, "countAll");
  const bdcArr = byMonth(bdcCounts, "countAll");

  // ── year N-1 ──
  const factureHT_p = byIdMonth(factureCA_p, "totalAmountSellingPrice");
  const factureTT_p = byIdMonth(factureCA_p, "totalAmountPriceTT");
  const bdcHT_p = byIdMonth(bdcCA_p, "totalAmountSellingPrice");
  const bdcTT_p = byIdMonth(bdcCA_p, "totalAmountPriceTT");
  const devisArr_p = byMonth(devisCounts_p, "countAll");
  const bdcArr_p = byMonth(bdcCounts_p, "countAll");

  const totalFactureHT = mergedSum(factureHT, computePrevision(factureHT));
  const totalFactureTT = mergedSum(factureTT, computePrevision(factureTT));
  const totalBdcHT = mergedSum(bdcHT, computePrevision(bdcHT));
  const totalDevis = mergedSum(devisArr, computePrevision(devisArr));
  const totalBdc = mergedSum(bdcArr, computePrevision(bdcArr));

  const sum = (a) => a.reduce((x, y) => x + y, 0);
  const payedCount = (list) =>
    (Array.isArray(list) ? list.find((f) => f?.id === 1) : null)?.count || 0;
  const unpayedCount = (list) =>
    (Array.isArray(list) ? list.find((f) => f?.id === 0) : null)?.count || 0;

  const totalPayed = payedCount(facturePayed);
  const totalPayed_p = payedCount(facturePayed_p);
  const totalUnpayed = unpayedCount(facturePayed);
  const totalUnpayed_p = unpayedCount(facturePayed_p);

  const prevYear = year - 1;

  const kpiCards = [
    { icon: "fas fa-file-invoice-dollar", bg: "bg-dark", label: "CA Factures HT", curr: totalFactureHT, prev: sum(factureHT_p) },
    { icon: "fas fa-dollar-sign", bg: "bg-gradient-info", label: "CA Factures TTC", curr: totalFactureTT, prev: sum(factureTT_p) },
    { icon: "fas fa-shopping-cart", bg: "bg-dark", label: "CA BDC HT", curr: totalBdcHT, prev: sum(bdcHT_p) },
    { icon: "fas fa-check-circle", bg: "bg-gradient-success", label: "Factures payées", curr: totalPayed, prev: totalPayed_p },
    { icon: "fas fa-exclamation-circle", bg: "bg-gradient-danger", label: "Factures non payées", curr: totalUnpayed, prev: totalUnpayed_p },
    { icon: "fas fa-file-alt", bg: "bg-dark", label: "Nb Devis", curr: totalDevis, prev: sum(devisArr_p) },
    { icon: "fas fa-clipboard-list", bg: "bg-gradient-warning", label: "Nb BDC", curr: totalBdc, prev: sum(bdcArr_p) },
  ];

  const barFactureHT = buildChart(factureHT, factureHT_p, year, prevYear, "rgba(30,136,229,0.85)", "rgba(30,136,229,0.25)", "rgba(30,136,229,0.3)");
  const barFactureTT = buildChart(factureTT, factureTT_p, year, prevYear, "rgba(0,188,212,0.85)", "rgba(0,188,212,0.25)", "rgba(0,188,212,0.3)");
  const barBdcHT = buildChart(bdcHT, bdcHT_p, year, prevYear, "rgba(255,152,0,0.85)", "rgba(255,152,0,0.25)", "rgba(255,152,0,0.3)");
  const barBdcTT = buildChart(bdcTT, bdcTT_p, year, prevYear, "rgba(255,87,34,0.85)", "rgba(255,87,34,0.25)", "rgba(255,87,34,0.3)");
  const barDevis = buildChart(devisArr, devisArr_p, year, prevYear, "rgba(76,175,80,0.85)", "rgba(76,175,80,0.25)", "rgba(76,175,80,0.3)");
  const barBdcCount = buildChart(bdcArr, bdcArr_p, year, prevYear, "rgba(156,39,176,0.85)", "rgba(156,39,176,0.25)", "rgba(156,39,176,0.3)");

  const doughnutFacture = {
    labels: ["CA HT", "TVA"],
    datasets: [
      {
        data: [fmt3(totalFactureHT), fmt3(totalFactureTT - totalFactureHT)],
        backgroundColor: ["#1e88e5", "#e53935"],
      },
    ],
  };

  const doughnutPayment = {
    labels: ["Payées", "Non payées"],
    datasets: [
      {
        data: [totalPayed, totalUnpayed],
        backgroundColor: ["#28a745", "#dc3545"],
      },
    ],
  };

  const opts = chartOpts();
  const doughnutOpts = { responsive: true, plugins: { legend: { position: "top" } } };

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6"></div>
            <div className="col-md-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/" style={{ color: "black" }}>
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active" style={{ color: "black" }}>
                  Les KPI de Vente
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {/* Header */}
          <div className="card card-dark mb-3">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
              <h3 className="card-title">
                <i className="fas fa-chart-bar mr-2"></i>
                KPI Ventes B2B &nbsp;
                <span className="badge badge-light">{year}</span>
                <span className="text-muted mx-2" style={{ fontSize: "0.85rem" }}>vs</span>
                <span className="badge badge-secondary">{prevYear}</span>
              </h3>
              <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                <span className="badge badge-warning" style={{ fontSize: "0.75rem" }}>
                  <i className="fas fa-magic mr-1"></i> Barres claires = prévision par régression
                </span>
                <div style={{ width: 140, color: "black" }}>
                  <Select
                    value={selectedYear}
                    options={yearsOptions}
                    onChange={(opt) => setSelectedYear(opt)}
                    placeholder={currentYear}
                  />
                </div>
                <button
                  className="btn btn-sm btn-outline-light"
                  onClick={() => fetchDashboard(Number(selectedYear?.label) || currentYear)}
                >
                  <i className="fas fa-sync-alt mr-1"></i> Valider
                </button>
                <button className="btn btn-sm btn-light" onClick={() => navigate("/KPISaleDevis")}>
                  Voir Devis
                </button>
                <button className="btn btn-sm btn-light" onClick={() => navigate("/kpisaleproduct")}>
                  Voir les produits
                </button>
              </div>
            </div>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {dashLoading ? (
            <div style={{ minHeight: 300 }} />
          ) : (
            <>
              {/* KPI cards */}
              <div className="row">
                {kpiCards.map((k, i) => (
                  <div className="col-lg-3 col-md-4 col-sm-6 col-12" key={i}>
                    <div className={`info-box ${k.bg}`}>
                      <span className="info-box-icon">
                        <i className={k.icon}></i>
                      </span>
                      <div className="info-box-content">
                        <span className="info-box-text">{k.label}</span>
                        <span className="info-box-number">{fmt3(k.curr)}</span>
                        <div className="progress">
                          <div className="progress-bar" style={{ width: "100%" }} />
                        </div>
                        <span className="progress-description d-flex align-items-center" style={{ gap: 6 }}>
                          <span style={{ fontSize: "0.75rem", opacity: 0.85 }}>
                            {prevYear} : {fmt3(k.prev)}
                          </span>
                          <EvoBadge curr={k.curr} prev={k.prev} />
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts */}
              <div className="row mt-3">
                <ChartCard title="CA Factures H.T">
                  <Bar data={barFactureHT} options={opts} />
                </ChartCard>
                <ChartCard title="CA Factures T.T">
                  <Bar data={barFactureTT} options={opts} />
                </ChartCard>
              </div>

              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="card card-dark">
                    <div className="card-header">
                      <h3 className="card-title">Répartition HT / TVA ({year} — avec prévisions)</h3>
                    </div>
                    <div className="card-body" style={{ maxWidth: 360, margin: "0 auto" }}>
                      <Doughnut data={doughnutFacture} options={doughnutOpts} />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card card-dark">
                    <div className="card-header">
                      <h3 className="card-title">Factures payées / non payées ({year})</h3>
                    </div>
                    <div className="card-body" style={{ maxWidth: 360, margin: "0 auto" }}>
                      <Doughnut data={doughnutPayment} options={doughnutOpts} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <ChartCard title="CA BDC H.T">
                  <Bar data={barBdcHT} options={opts} />
                </ChartCard>
                <ChartCard title="CA BDC T.T">
                  <Bar data={barBdcTT} options={opts} />
                </ChartCard>
              </div>

              <div className="row mt-3 mb-4">
                <ChartCard title="Nombre de Devis / mois">
                  <Bar data={barDevis} options={opts} />
                </ChartCard>
                <ChartCard title="Nombre de BDC / mois">
                  <Bar data={barBdcCount} options={opts} />
                </ChartCard>
              </div>

              {/* Detailed tables */}
              <KpiSaleB2B key={year} finalYear={year} />
            </>
          )}
        </div>
      </section>
    </div>
  );
};
export default withLoader(KPISale);
